/* ============================================================================
   K•SOCIETY — global.js
   Progressive enhancement. Core shop flows must keep working without JS where
   Shopify provides a native fallback (forms post normally, links resolve).
   ========================================================================== */
(function () {
  'use strict';

  const routes = window.routes || {};
  const strings = window.themeStrings || {};

  /* --------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  const focusableSelector =
    'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, details, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, initial) {
    const focusables = () =>
      Array.from(container.querySelectorAll(focusableSelector)).filter(
        (el) => el.offsetParent !== null
      );
    (initial || focusables()[0] || container).focus({ preventScroll: true });

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return () => container.removeEventListener('keydown', onKeydown);
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  }

  const money = (cents) => {
    try {
      return (cents / 100).toLocaleString(document.documentElement.lang || 'de', {
        style: 'currency',
        currency: window.shopCurrency || 'EUR'
      });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  /* --------------------------------------------------------------------------
     Pub/sub — decouple cart updates from UI
     ------------------------------------------------------------------------ */
  const bus = {
    events: {},
    on(name, cb) {
      (this.events[name] = this.events[name] || []).push(cb);
      return () => {
        this.events[name] = this.events[name].filter((fn) => fn !== cb);
      };
    },
    emit(name, detail) {
      (this.events[name] || []).forEach((cb) => cb(detail));
    }
  };
  window.KS = { bus, money };

  /* --------------------------------------------------------------------------
     Cart section rendering — single source of truth for live cart UI
     ------------------------------------------------------------------------ */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function swapSections(sectionsHtml) {
    if (!sectionsHtml) return;
    Object.keys(sectionsHtml).forEach((key) => {
      const doc = new DOMParser().parseFromString(sectionsHtml[key], 'text/html');
      const source = doc.querySelector(`[data-section-render="${key}"]`);
      document.querySelectorAll(`[data-section-render="${key}"]`).forEach((target) => {
        if (source) target.innerHTML = source.innerHTML;
      });
    });
  }

  async function refreshCart(prefetchedSections) {
    // The GET ?sections= API returns a flat { "<section-id>": "<html>" } object.
    if (prefetchedSections) {
      swapSections(prefetchedSections);
    } else {
      const sections = ['cart-drawer'];
      if (document.querySelector('[data-section-render="main-cart"]')) sections.push('main-cart');
      try {
        const res = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`);
        swapSections(await res.json());
      } catch (e) {
        /* leave existing markup untouched on failure */
      }
    }
    try {
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      updateCartCount(cart.item_count);
      bus.emit('cart:updated', cart);
    } catch (e) {
      /* ignore */
    }
    bus.emit('cart:refreshed');
  }

  window.KS.refreshCart = refreshCart;

  /* --------------------------------------------------------------------------
     Overlay + body scroll lock (shared across drawers/modals)
     ------------------------------------------------------------------------ */
  let openLayerCount = 0;
  const overlay = document.getElementById('Overlay');

  function lockScroll() {
    openLayerCount += 1;
    document.body.classList.add('overflow-hidden');
    if (overlay) overlay.classList.add('is-active');
  }
  function unlockScroll() {
    openLayerCount = Math.max(0, openLayerCount - 1);
    if (openLayerCount === 0) {
      document.body.classList.remove('overflow-hidden');
      if (overlay) overlay.classList.remove('is-active');
    }
  }

  /* --------------------------------------------------------------------------
     Base drawer element
     ------------------------------------------------------------------------ */
  class DrawerElement extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.lastActive = null;
      this.onOverlayClick = this.close.bind(this);
      this.onKeydown = (e) => {
        if (e.key === 'Escape') this.close();
      };
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]') || this;
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      const id = this.getAttribute('id');
      document
        .querySelectorAll(`[data-drawer-open="${id}"]`)
        .forEach((btn) => btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        }));
    }

    open(trigger) {
      this.lastActive = trigger || document.activeElement;
      this.setAttribute('open', '');
      this.panel.classList.add('is-open');
      lockScroll();
      if (overlay) overlay.addEventListener('click', this.onOverlayClick);
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => {
        this.releaseFocus = trapFocus(
          this.panel,
          this.panel.querySelector('[data-drawer-initial-focus]')
        );
      });
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      this.removeAttribute('open');
      this.panel.classList.remove('is-open');
      unlockScroll();
      if (overlay) overlay.removeEventListener('click', this.onOverlayClick);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastActive && this.lastActive.focus) {
        this.lastActive.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }

  class MenuDrawer extends DrawerElement {}
  class SearchDrawer extends DrawerElement {
    open(trigger) {
      super.open(trigger);
      const input = this.querySelector('input[type="search"]');
      if (input) setTimeout(() => input.focus(), 60);
    }
  }
  class CartDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      bus.on('cart:add', () => {
        if (!this.hasAttribute('open')) this.open();
      });
      bus.on('cart:refreshed', () => {
        // keep focus sane after the drawer contents are swapped
        if (this.hasAttribute('open')) {
          const heading = this.querySelector('.drawer__title');
          if (heading) heading.setAttribute('tabindex', '-1'), heading.focus({ preventScroll: true });
        }
      });
    }
  }

  customElements.define('menu-drawer', MenuDrawer);
  customElements.define('search-drawer', SearchDrawer);
  customElements.define('cart-drawer', CartDrawer);

  /* --------------------------------------------------------------------------
     Modal dialog (size guide, etc.)
     ------------------------------------------------------------------------ */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-modal-content]');
      this.releaseFocus = null;
      this.lastActive = null;
      this.querySelectorAll('[data-modal-close]').forEach((b) =>
        b.addEventListener('click', () => this.close())
      );
      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });
      this.onKeydown = (e) => e.key === 'Escape' && this.close();
      const id = this.getAttribute('id');
      document.querySelectorAll(`[data-modal-open="${id}"]`).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        })
      );
    }

    open(trigger) {
      this.lastActive = trigger || document.activeElement;
      this.removeAttribute('hidden');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      this.releaseFocus = trapFocus(this.dialog);
    }

    close() {
      this.setAttribute('hidden', '');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastActive && this.lastActive.focus) this.lastActive.focus();
    }
  }
  customElements.define('modal-dialog', ModalDialog);

  /* --------------------------------------------------------------------------
     Quantity input
     ------------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', () => {
          const step = btn.dataset.step === 'down' ? -1 : 1;
          const min = parseInt(this.input.min || '1', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          let value = parseInt(this.input.value || '1', 10) + step;
          value = Math.min(max, Math.max(min, value));
          if (String(value) !== this.input.value) {
            this.input.value = value;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* --------------------------------------------------------------------------
     Cart items — quantity change / remove (drawer + cart page)
     ------------------------------------------------------------------------ */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const line = e.target.closest('[data-cart-line]');
        if (line && e.target.matches('input[type="number"]')) {
          this.updateLine(line.dataset.cartLine, e.target.value);
        }
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.updateLine(remove.dataset.cartRemove, 0);
        }
      });
    }

    async updateLine(lineKey, quantity) {
      this.classList.add('is-loading');
      try {
        const res = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ id: lineKey, quantity: Number(quantity) })
        });
        const state = await res.json();
        if (state.status) {
          this.showError(state.description || strings.cartError);
          return;
        }
        await refreshCart();
      } catch (e) {
        this.showError(strings.cartError);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    showError(message) {
      const region = this.querySelector('[data-cart-errors]');
      if (region) {
        region.textContent = message;
        region.hidden = false;
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* --------------------------------------------------------------------------
     Product form — AJAX add to cart
     ------------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(event) {
      if (this.form.querySelector('[name="id"]')?.disabled) return;
      event.preventDefault();
      // One add at a time, whichever button (main CTA or mobile sticky bar) was tapped.
      if (this.busy) return;
      this.busy = true;
      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      const formData = new FormData(this.form);

      try {
        const res = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();
        if (data.status) {
          this.showError(data.description || data.message || strings.cartError);
          return;
        }
        await refreshCart();
        bus.emit('cart:add', data);
        const live = document.querySelector('[data-a11y-live]');
        if (live) live.textContent = strings.addedToCart || '';
      } catch (e) {
        this.showError(strings.cartError);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      if (!state) this.busy = false;
      // Buttons bound to this form from outside it (mobile sticky add-to-bag) too.
      const external = this.form && this.form.id
        ? Array.from(document.querySelectorAll(`[form="${this.form.id}"][type="submit"]`))
        : [];
      external.forEach((b) => b.setAttribute('aria-busy', state ? 'true' : 'false'));
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('button--loading', state);
      this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      } else {
        this.form.submit();
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* --------------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      if (!this.input) return;
      this.debounced = this.debounce(() => this.search(), 250);
      this.input.addEventListener('input', this.debounced);
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        if (this.status) this.status.textContent = '';
        return;
      }
      if (this.status) this.status.textContent = strings.searchLoading || '';
      try {
        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(
          q
        )}&section_id=predictive-search`;
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('[data-predictive-results]');
        this.results.innerHTML = content ? content.innerHTML : '';
        if (this.status) {
          const count = this.results.querySelectorAll('a').length;
          this.status.textContent = (strings.searchResults || '{count}').replace(
            '{count}',
            count
          );
        }
      } catch (e) {
        this.results.innerHTML = '';
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* --------------------------------------------------------------------------
     Accordion group — keep native <details>, optionally single-open
     ------------------------------------------------------------------------ */
  document.querySelectorAll('[data-accordion-single]').forEach((group) => {
    group.addEventListener('toggle', (e) => {
      if (e.target.open) {
        group
          .querySelectorAll('details[open]')
          .forEach((d) => d !== e.target && (d.open = false));
      }
    }, true);
  });

  /* --------------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------------ */
  if (
    'IntersectionObserver' in window &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    document.documentElement.dataset.revealAnimations !== 'false'
  ) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    const observeReveals = (scope) =>
      (scope || document).querySelectorAll('.reveal:not(.is-visible)').forEach((el) => io.observe(el));
    observeReveals();
    document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));
  } else {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* --------------------------------------------------------------------------
     Announcement / small helpers for Theme Editor
     ------------------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('quantity-input').forEach((el) => customElements.upgrade(el));
  });

  /* --------------------------------------------------------------------------
     Newsletter forms — guard against double submissions while the native
     POST is in flight. Delegated (works for every .newsletter-form, incl.
     ones re-rendered by the Theme Editor), fires only once the browser's
     own required/type=email validation has already passed, and never
     intercepts or replaces the native submission itself.
     ------------------------------------------------------------------------ */
  document.addEventListener('submit', (e) => {
    if (!e.target.classList || !e.target.classList.contains('newsletter-form')) return;
    const btn = e.target.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
  });

  /* --------------------------------------------------------------------------
     Footer nav accordions — <details open> in the markup so desktop/tablet
     show the columns expanded with zero JS. On mobile they should start
     collapsed instead; that's the one thing CSS can't do (the open
     attribute is real DOM/AX state), so this just removes it once, on
     mobile only. The actual expand/collapse interaction is fully native
     <details> behaviour — no JS involved in that at all. Re-runs on Theme
     Editor section reloads so freshly rendered blocks are collapsed too.
     ------------------------------------------------------------------------ */
  const collapseFooterAccordions = (scope) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    (scope || document).querySelectorAll('.footer__accordion[open]').forEach((el) => {
      el.removeAttribute('open');
    });
  };
  collapseFooterAccordions();
  document.addEventListener('shopify:section:load', (e) => collapseFooterAccordions(e.target));

  /* --------------------------------------------------------------------------
     Footer "Cookie-Einstellungen" — reopens Shopify's native privacy/consent
     preferences window (window.privacyBanner.showPreferences()), including
     after a visitor has already rejected cookies. A real <button> (see
     snippets/footer-link.liquid), so there is no href/navigation to guard
     against — no page-jump, no reload, regardless of the API's state.
     Delegated on document, so it keeps working after any section reload.
     Shopify's privacy script can still be loading on first paint, so a
     short, bounded retry covers that instead of failing silently on the
     very first click. ------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cookie-preferences]');
    if (!trigger) return;

    const openPreferences = (attemptsLeft) => {
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
        return;
      }
      if (attemptsLeft > 0) {
        setTimeout(() => openPreferences(attemptsLeft - 1), 200);
        return;
      }
      if (window.console && console.warn) {
        console.warn('K•SOCIETY: window.privacyBanner.showPreferences() was not available.');
      }
    };
    openPreferences(10);
  });

  /* --------------------------------------------------------------------------
     Cross-page hash anchors (e.g. /#newsletter from the Society page's "JOIN
     THE SOCIETY" button). "html { scroll-behavior: smooth }" (base.css §1)
     makes the browser's native on-load anchor jump animate instead of snap —
     if images/layout above the target are still shifting while that
     animation runs, it finishes in the wrong place (typically back at the
     hero). Re-confirms the position once the whole page (incl. images) has
     actually finished loading. No-op when there is no hash, same-page
     clicks are untouched (native browser behaviour already handles those).
     ------------------------------------------------------------------------ */
  if (location.hash) {
    window.addEventListener('load', () => {
      try {
        const target = document.querySelector(location.hash);
        if (target) target.scrollIntoView({ block: 'start' });
      } catch (err) {
        /* malformed hash — ignore, never breaks the page */
      }
    });
  }

  /* --------------------------------------------------------------------------
     Collection toolbar — FILTER / SORTIEREN (sections/main-collection.liquid).
     Plain <div data-toolbar-group> / <button data-toolbar-trigger> /
     <div data-toolbar-panel>, NOT <details> — a native <details> hides its
     non-summary content through a browser-internal mechanism that could not
     be reliably forced back open with author CSS on desktop (that was a
     real, shipped regression: the whole toolbar vanished at >=750px).
     `hidden` is the same already-proven pattern as the quick-add panel
     above, just toggled here based on actual viewport instead of a click
     alone, since desktop must always show the content regardless of click
     state. No-JS fallback: the panel never has `hidden` in the server
     HTML, so without JS everything stays visible everywhere — degraded,
     never broken. ------------------------------------------------------- */
  const mqToolbarMobile = window.matchMedia('(max-width: 749px)');

  function syncToolbarPanels(scope) {
    (scope || document).querySelectorAll('[data-toolbar-group]').forEach((group) => {
      const trigger = group.querySelector('[data-toolbar-trigger]');
      const panel = group.querySelector('[data-toolbar-panel]');
      if (!trigger || !panel) return;
      if (mqToolbarMobile.matches) {
        if (!panel.hasAttribute('hidden')) {
          panel.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        }
      } else {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  }
  syncToolbarPanels();
  mqToolbarMobile.addEventListener('change', () => syncToolbarPanels());
  document.addEventListener('shopify:section:load', (e) => syncToolbarPanels(e.target));

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-toolbar-trigger]');
    if (!trigger || !mqToolbarMobile.matches) return;
    const group = trigger.closest('[data-toolbar-group]');
    const panel = group && group.querySelector('[data-toolbar-panel]');
    if (!panel) return;
    const wasHidden = panel.hasAttribute('hidden');
    panel.hidden = !wasHidden;
    trigger.setAttribute('aria-expanded', String(wasHidden));
  });
})();
